import React, { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { X, Loader2 } from 'lucide-react';
import { getCroppedProfileFile } from '../../utils/cropProfileImage';
import './ProfilePhotoCropModal.css';

interface ProfilePhotoCropModalProps {
  imageSrc: string;
  onCancel: () => void;
  onSave: (file: File) => Promise<void>;
}

/**
 * Drag to reposition, zoom slider — exports a square JPEG for upload.
 */
const ProfilePhotoCropModal: React.FC<ProfilePhotoCropModalProps> = ({ imageSrc, onCancel, onSave }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) {
      setError('Still loading crop — try again in a moment.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const file = await getCroppedProfileFile(imageSrc, croppedAreaPixels);
      await onSave(file);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Could not save photo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-crop-overlay" role="dialog" aria-modal="true" aria-labelledby="profile-crop-title">
      <div className="profile-crop-panel">
        <div className="profile-crop-header">
          <h2 id="profile-crop-title">Adjust photo</h2>
          <button type="button" className="profile-crop-close" onClick={onCancel} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <p className="profile-crop-hint">Drag to reposition. Use the slider to zoom.</p>

        <div className="profile-crop-stage">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="profile-crop-zoom">
          <label htmlFor="profile-crop-zoom-range">Zoom</label>
          <input
            id="profile-crop-zoom-range"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
          />
        </div>

        {error && <p className="profile-crop-error">{error}</p>}

        <div className="profile-crop-actions">
          <button type="button" className="profile-crop-btn profile-crop-btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="profile-crop-btn profile-crop-btn-primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Loader2 size={16} className="profile-crop-spin" /> : null}
            Save photo
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoCropModal;
